/**
 * What `/fly` was asked to fly, resolved from the query string.
 *
 * Three things can arrive at that route: nothing (the instant dogfight), a
 * single mission (`?m=<catalog id>&seed=<n>`, regenerated by
 * `mission-launch.ts`), or a career pilot (`?career=<id>`, whose next
 * assignment is regenerated by `career-session.ts`). They differ in exactly
 * three ways — which spec is flown, where the exit link goes, and whether the
 * debriefing is credited to anybody — so all three are settled here, once,
 * rather than being three conditionals threaded through the route.
 */

import { playerFlightOf, type CareerDate, type MissionDebrief, type MissionSpec } from "@red-opus/engine";
import type { PersonalPaint } from "@red-opus/formats";
import { careerPaint } from "../career/career-paint.ts";
import { careerSortie, landSortie, parseCareerLaunch } from "../career/career-session.ts";
import type { CareerStore } from "../career/career-store.ts";
import { missionFor, parseMissionLaunch, type SearchLike } from "../mission/mission-launch.ts";

export interface FlyTarget {
  readonly mission: MissionSpec | undefined;
  /** Set only for a career sortie. */
  readonly pilotId: string | undefined;
  /**
   * The pilot's paint-shop choices, when he is flying his own painted machine.
   * Absent for a squadron aircraft, an unpainted personal one, and every
   * non-career flight — all of which draw the factory scheme they were baked in.
   */
  readonly playerPaint: PersonalPaint | undefined;
  /**
   * The pilot's own calendar date, for a career sortie. Set nowhere else: a
   * single mission has only an era and the dogfight has nothing. The ENHANCED
   * night sky is the one thing that reads it (`night-sky-look.ts`).
   */
  readonly date: CareerDate | undefined;
  /**
   * Which service the pilot flies for, which is the emblem the debriefing's
   * header band wears — an iron cross or an RFC roundel. Read off the career for
   * a career sortie and off the mission for a single one; the bare instant
   * dogfight has no side to speak of and defaults to the Allied roundel.
   */
  readonly nation: "german" | "allied";
  /** Where the corner link and the debriefing's CONTINUE go. */
  readonly exitPath: string;
  readonly exitLabel: string;
  /** Applies the debriefing to whoever owns it. A no-op outside a career. */
  readonly credit: (debrief: MissionDebrief) => void;
}

export function resolveFlyTarget(search: SearchLike, store: CareerStore | undefined): FlyTarget {
  const pilotId = parseCareerLaunch(search);
  const sortie = store && pilotId ? careerSortie(store, pilotId) : undefined;
  if (sortie && store && pilotId) {
    const personal = sortie.save.career.personalAircraft;
    const flying = playerFlightOf(sortie.assignment.spec).aircraft;
    return {
      mission: sortie.assignment.spec,
      pilotId,
      // Only his own machine wears his colours: when the squadron puts him in
      // something else the aeroplane is the squadron's, and so is its paint.
      playerPaint: personal && personal.aircraft === flying ? careerPaint(sortie.save.career) : undefined,
      date: sortie.save.career.date,
      nation: sortie.save.career.nation,
      exitPath: "/career",
      exitLabel: "Aerodrome",
      // Credited once, on the tick the mission is decided, and written straight
      // to the save — the career screen reads what happened out of storage
      // rather than out of navigation state a reload would lose.
      credit: (debrief) => void landSortie(store, pilotId, debrief),
    };
  }
  const launch = parseMissionLaunch(search);
  const mission = launch ? missionFor(launch) : undefined;
  return {
    mission,
    pilotId: undefined,
    playerPaint: undefined,
    date: undefined,
    nation: mission?.playerNation ?? "allied",
    exitPath: mission ? "/missions" : "/",
    exitLabel: mission ? "Missions" : "Title",
    credit: () => undefined,
  };
}
